import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

// Storage key constants
const STORAGE_KEYS = {
  TOKEN: 'rooam_token',
  USER: 'rooam_user',
  TOKEN_EXPIRY: 'rooam_token_expiry',
};

// Token expiry time (24 hours in milliseconds)
const TOKEN_EXPIRY_MS = 24 * 60 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Secure storage wrapper
 * - Uses sessionStorage for sensitive data when possible
 * - Validates token expiry
 * - Clears data on browser close (sessionStorage) or expiry
 */
const secureStorage = {
  setToken: (token) => {
    try {
      const expiry = Date.now() + TOKEN_EXPIRY_MS;
      // Use sessionStorage for token (cleared on browser close)
      sessionStorage.setItem(STORAGE_KEYS.TOKEN, token);
      sessionStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiry.toString());
      // Also store in localStorage for "remember me" functionality
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiry.toString());
    } catch (e) {
      console.error('Failed to store token:', e);
    }
  },
  
  getToken: () => {
    try {
      // Try sessionStorage first (more secure)
      let token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      let expiry = sessionStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      
      // Fall back to localStorage
      if (!token) {
        token = localStorage.getItem(STORAGE_KEYS.TOKEN);
        expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
      }
      
      // Check expiry
      if (token && expiry) {
        if (Date.now() > parseInt(expiry, 10)) {
          // Token expired, clear it
          secureStorage.clearAll();
          return null;
        }
        return token;
      }
      return null;
    } catch (e) {
      console.error('Failed to get token:', e);
      return null;
    }
  },
  
  setUser: (user) => {
    try {
      const userStr = JSON.stringify(user);
      sessionStorage.setItem(STORAGE_KEYS.USER, userStr);
      localStorage.setItem(STORAGE_KEYS.USER, userStr);
    } catch (e) {
      console.error('Failed to store user:', e);
    }
  },
  
  getUser: () => {
    try {
      let userStr = sessionStorage.getItem(STORAGE_KEYS.USER);
      if (!userStr) {
        userStr = localStorage.getItem(STORAGE_KEYS.USER);
      }
      return userStr ? JSON.parse(userStr) : null;
    } catch (e) {
      console.error('Failed to get user:', e);
      return null;
    }
  },
  
  clearAll: () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.error('Failed to clear storage:', e);
    }
  },
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Validate token with backend
  const validateToken = useCallback(async (storedToken) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/validate`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        },
      });
      return response.ok;
    } catch (error) {
      console.error('Token validation failed:', error);
      return false;
    }
  }, [API_URL]);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = secureStorage.getToken();
      const storedUser = secureStorage.getUser();
      
      if (storedToken && storedUser) {
        // Optionally validate token with backend
        // For performance, we skip validation on every load
        // and rely on API calls to catch invalid tokens
        setToken(storedToken);
        setUser(storedUser);
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), password }),
      });

      let data;
      try {
        data = await response.json();
      } catch (parseErr) {
        throw new Error('Server response error. Please try again.');
      }

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store securely
      secureStorage.setToken(data.access_token);
      secureStorage.setUser(data.user);
      setToken(data.access_token);
      setUser(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      console.error('Login error:', error);
      let errorMessage = 'Login failed';
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      return { success: false, error: errorMessage };
    }
  };

  const register = async (name, email, password, role, discountCode = null, signupSource = null) => {
    try {
      const body = { name, email, password, role };
      if (discountCode) body.discount_code = discountCode;
      if (signupSource) body.signup_source = signupSource;

      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }

      secureStorage.setToken(data.access_token);
      secureStorage.setUser(data.user);
      setToken(data.access_token);
      setUser(data.user);
      
      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = useCallback(() => {
    secureStorage.clearAll();
    setToken(null);
    setUser(null);
  }, []);

  // Auto-logout on token expiry check (runs every minute)
  useEffect(() => {
    const checkExpiry = () => {
      const storedToken = secureStorage.getToken();
      if (!storedToken && token) {
        // Token was cleared due to expiry
        logout();
      }
    };
    
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [token, logout]);

  const isSuperAdmin = () => {
    return user?.role === 'super_admin';
  };

  const isAdmin = () => {
    return user?.role === 'admin' || user?.role === 'super_admin';
  };

  const isOperator = () => {
    return user?.role === 'operator' || user?.role === 'admin' || user?.role === 'super_admin';
  };

  const canAccessAdmin = () => {
    return ['admin', 'super_admin', 'operator'].includes(user?.role);
  };

  const isAuthenticated = () => {
    return !!user && !!token;
  };

  const isPendingPayment = () => {
    return user?.subscription_status === 'pending_payment';
  };

  const isCustomerRole = () => {
    return ['athlete', 'coach', 'parent', 'scout', 'college_coach'].includes(user?.role);
  };

  const getSubscriptionTier = () => {
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'operator') return 'elite';
    
    if (!user?.subscription?.plan_id) return 'free';
    const planId = user.subscription.plan_id;
    if (planId.startsWith('elite')) return 'elite';
    if (planId.startsWith('plus')) return 'plus';
    if (planId.startsWith('basic')) return 'basic';
    return 'free';
  };

  const hasRequiredTier = (requiredTier) => {
    if (user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'operator') return true;
    
    const tierHierarchy = { free: 0, basic: 1, plus: 2, elite: 3 };
    const userTier = getSubscriptionTier();
    return tierHierarchy[userTier] >= tierHierarchy[requiredTier];
  };

  // Helper to refresh user data from backend (e.g., after payment)
  const refreshUser = async () => {
    try {
      const currentToken = secureStorage.getToken();
      if (!currentToken) return;
      const response = await fetch(`${API_URL}/api/auth/me`, {
        headers: { 'Authorization': `Bearer ${currentToken}` },
      });
      if (response.ok) {
        const userData = await response.json();
        secureStorage.setUser(userData);
        setUser(userData);
      }
    } catch (e) {
      console.error('Failed to refresh user:', e);
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isSuperAdmin,
    isAdmin,
    isOperator,
    canAccessAdmin,
    isAuthenticated,
    isPendingPayment,
    isCustomerRole,
    getSubscriptionTier,
    hasRequiredTier,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
